<template>
  <div v-if="releaseSlides?.length" class="release-carousel" data-cy="release-carousel">
    <swiper-container
      ref="swiperEl"
      :slides-per-view="1"
      :speed="500"
      :loop="true"
      :pagination="{
        clickable: true,
      }"
      v-on:swiperslidechange="onSlideChange">
      <swiper-slide v-for="slide in releaseSlides" :key="slide.externalId">
        <div class="image" data-cy="release-image" :style="{ backgroundImage: `url(${slide.imageUrl})` }"></div>
        <div class="text">
          <div class="title" data-cy="release-title">
            {{ slide.title[userLanguage] || slide.title[fallbackLocale] }}
          </div>
        </div>
      </swiper-slide>
    </swiper-container>
    <div class="footer">
      <div>
        <div v-if="isLastSlide">
          <a class="finish-button" data-cy="release-finish-button">
            <BackstageButton v-on:click="completeNewRelease">
              {{ $t('newRelease.confirm_button_label') }}
            </BackstageButton>
          </a>
        </div>
        <div v-else>
          <button class="nav-button prev-button" data-cy="release-cursor-prev" v-on:click="prev">
            <i class="first-icon-plus" />
          </button>
          <button class="nav-button next-button" data-cy="release-cursor-next" v-on:click="next">
            <i class="second-icon-plus" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { utilsMixins } from '@/mixins/utils.mixins';
import { FeatureReleaseModel } from '@onrunning/backstage-models';
import { UsersService } from '@/services/users.service';
import { useLocale } from '../../composables/locale';
import { globalConstants } from '../../constants/global.constants';
import BackstageButton from '@/components/common/BackstageButton.vue';

export default defineComponent({
  name: 'release-carousel',
  components: {
    BackstageButton,
  },
  props: {
    newRelease: {
      required: false,
      type: FeatureReleaseModel,
      default: undefined,
    },
  },
  setup() {
    const { userLanguage } = useLocale();

    return {
      userLanguage,
      fallbackLocale: globalConstants.fallBackLocale,
    };
  },
  data() {
    return {
      featureRelease: this.newRelease,
      currentSlideIndex: 0,
      releaseSlides: this.newRelease?.slides,
      userService: new UsersService(),
    };
  },
  computed: {
    isLastSlide(): boolean {
      return this.releaseSlides?.length ? this.releaseSlides?.length - 1 === this.currentSlideIndex : false;
    },
  },
  methods: {
    ...utilsMixins,
    completeNewRelease() {
      if (this.featureRelease) {
        this.userService.updateRelease(this.featureRelease.externalId);
        this.$emit('completeNewRelease');
      }
    },
    next() {
      (this.$refs.swiperEl as any).swiper.slideNext();
    },
    prev() {
      (this.$refs.swiperEl as any).swiper.slidePrev();
    },
    onSlideChange(event: { detail: { activeIndex: number }[] }) {
      this.currentSlideIndex = event.detail[0].activeIndex;
    },
  },
});
</script>

<style scoped lang="scss">
.release-carousel:deep() {
  max-width: 820px;

  swiper-container {
    position: relative;
    display: block;
    box-sizing: border-box;
    user-select: none;
    touch-action: pan-y;
    overflow: hidden;
    --swiper-pagination-bottom: 10px;
    --swiper-pagination-color: var(--primary-button-bg-color);

    swiper-slide {
      padding-bottom: 80px;

      .image {
        position: relative;
        width: 100%;
        aspect-ratio: 2 / 1;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;

        .finish-button {
          display: none;
          margin-top: 8px;
          max-height: 30px;
        }
      }

      .text {
        color: var(--primary-button-bg-color);
        position: absolute;
        left: 0;
        bottom: 10px;
        max-width: 70%;
        text-align: left;

        .title {
          font-size: 1rem;
        }
      }
    }
  }

  .footer {
    position: absolute;
    right: 44px;
    bottom: 44px;
    z-index: 2;

    .nav-button {
      width: 42px;
      height: 42px;
      border-radius: 100%;
      border: none;
      background-repeat: no-repeat;
      background-position: center;
      background-color: var(--primary-button-bg-color);
      cursor: pointer;
    }

    .prev-button {
      background-image: url('@/assets/img/chevron-left.svg');
      background-size: 60%;
    }

    .next-button {
      background-image: url('@/assets/img/chevron-right.svg');
      background-size: 25%;
      margin-left: 12px;
    }
  }
}
</style>
